import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import Footer from '@/components/Footer';

export default function PrivacyPolicyPage() {
  const lastUpdated = "February 23, 2026";

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-navy text-white py-6 px-6 md:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-display text-xl">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center">
              <Shield className="text-teal" size={24} />
            </div>
            <div>
              <h1 className="font-display text-4xl text-navy">Privacy Policy</h1>
              <p className="text-text-mid text-sm mt-1">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl p-8 shadow-card space-y-8">
              
              {/* Introduction */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Introduction</h2>
                <p className="text-text-mid leading-relaxed">
                  Cali Moms Connect ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our membership platform and services. By accessing or using Cali Moms Connect, you agree to this Privacy Policy.
                </p>
                <p className="text-text-mid leading-relaxed mt-4">
                  We are a California-based community platform designed to help mothers connect with one another. We take your privacy seriously and are committed to maintaining the trust and confidence of our members.
                </p>
              </section>

              {/* Information We Collect */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Information We Collect</h2>
                
                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Personal Information You Provide</h3>
                <p className="text-text-mid leading-relaxed">When you create an account and use our services, we collect:</p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li><strong>Account Information:</strong> Name, email address, password, and profile photo</li>
                  <li><strong>Location Information:</strong> City of residence within California</li>
                  <li><strong>Family Information:</strong> Number of children and their age ranges</li>
                  <li><strong>Profile Details:</strong> Parenting style, values, interests, availability, friendship goals, and other preferences shared during onboarding</li>
                  <li><strong>Payment Information:</strong> Billing details processed through our payment provider, Stripe</li>
                  <li><strong>Communications:</strong> Messages sent through our platform to other members</li>
                </ul>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Information Collected Automatically</h3>
                <ul className="list-disc list-inside text-text-mid space-y-2 ml-4">
                  <li><strong>Usage Data:</strong> How you interact with our platform, features used, and pages visited</li>
                  <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
                  <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
                </ul>
              </section>

              {/* How We Use Your Information */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">How We Use Your Information</h2>
                <p className="text-text-mid leading-relaxed">We use the information we collect to:</p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li><strong>Facilitate Matching:</strong> Use your profile information to match you with compatible moms based on location, children's ages, interests, and lifestyle preferences</li>
                  <li><strong>Enable Communication:</strong> Allow you to message other members and participate in community discussions</li>
                  <li><strong>Personalize Your Experience:</strong> Customize content, event recommendations, and local business offers based on your location and preferences</li>
                  <li><strong>Process Payments:</strong> Handle membership subscriptions and billing through Stripe</li>
                  <li><strong>Send Communications:</strong> Deliver transactional emails, match notifications, event reminders, and platform updates</li>
                  <li><strong>Improve Our Services:</strong> Analyze usage patterns to enhance features and user experience</li>
                  <li><strong>Ensure Safety:</strong> Monitor for fraudulent activity and enforce our community guidelines</li>
                </ul>
              </section>

              {/* Information Sharing */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Information Sharing and Third Parties</h2>
                <p className="text-text-mid leading-relaxed">We share your information with the following third parties:</p>
                
                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Service Providers</h3>
                <ul className="list-disc list-inside text-text-mid space-y-2 ml-4">
                  <li><strong>Stripe:</strong> Processes payment transactions. Stripe receives your payment card details and billing information. See Stripe's privacy policy at stripe.com/privacy.</li>
                  <li><strong>SendGrid:</strong> Delivers transactional and notification emails on our behalf. SendGrid receives your email address and name. See SendGrid's privacy policy at sendgrid.com/policies/privacy.</li>
                </ul>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Other Members</h3>
                <p className="text-text-mid leading-relaxed">
                  Your profile information (name, city, children's ages, bio, interests) is visible to other members to facilitate connections. You control what information you share in your profile and messages.
                </p>

                <h3 className="font-semibold text-navy text-lg mt-6 mb-3">Legal Requirements</h3>
                <p className="text-text-mid leading-relaxed">
                  We may disclose your information if required by law, subpoena, or other legal process, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
                </p>
              </section>

              {/* Data Retention and Deletion */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Data Retention and Deletion</h2>
                <p className="text-text-mid leading-relaxed">
                  We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account and personal data at any time by:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Emailing us at <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a> with the subject line "Data Deletion Request"</li>
                  <li>Including your registered email address and name in the request</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  Upon receiving a valid deletion request, we will delete your personal data within 30 days, except for information we are required to retain for legal, accounting, or security purposes.
                </p>
              </section>

              {/* Cookies */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Cookie Policy</h2>
                <p className="text-text-mid leading-relaxed">
                  We use cookies and similar tracking technologies to improve your experience on our platform:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li><strong>Essential Cookies:</strong> Required for the platform to function, including authentication and session management</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how members use our platform to improve our services</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  You can control cookies through your browser settings. However, disabling essential cookies may prevent you from using certain features of our platform.
                </p>
              </section>

              {/* Children's Privacy */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Children's Privacy</h2>
                <p className="text-text-mid leading-relaxed">
                  Cali Moms Connect is intended for adults aged 18 and older. We do not knowingly collect personal information from anyone under the age of 18. If we learn that we have collected personal information from a child under 18, we will take steps to delete that information as quickly as possible.
                </p>
                <p className="text-text-mid leading-relaxed mt-4">
                  If you believe we have collected information from a minor, please contact us immediately at <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a>.
                </p>
              </section>

              {/* California Privacy Rights (CCPA) */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Your California Privacy Rights (CCPA)</h2>
                <p className="text-text-mid leading-relaxed">
                  As a California-based platform serving California residents, we comply with the California Consumer Privacy Act (CCPA). California residents have the following rights:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li><strong>Right to Know:</strong> You can request information about the categories and specific pieces of personal information we have collected about you, the sources of that information, our business purposes for collecting it, and the categories of third parties with whom we share it.</li>
                  <li><strong>Right to Delete:</strong> You can request deletion of your personal information, subject to certain exceptions.</li>
                  <li><strong>Right to Opt-Out:</strong> We do not sell your personal information. However, if this changes, you will have the right to opt-out of the sale of your personal information.</li>
                  <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  To exercise these rights, contact us at <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a>. We will respond to verifiable consumer requests within 45 days.
                </p>
              </section>

              {/* Security */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Data Security</h2>
                <p className="text-text-mid leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc list-inside text-text-mid space-y-2 mt-3 ml-4">
                  <li>Encryption of data in transit using SSL/TLS</li>
                  <li>Secure password hashing</li>
                  <li>Regular security assessments</li>
                  <li>Access controls limiting employee access to personal data</li>
                </ul>
                <p className="text-text-mid leading-relaxed mt-4">
                  However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
                </p>
              </section>

              {/* Changes to Policy */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Changes to This Privacy Policy</h2>
                <p className="text-text-mid leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="font-display text-2xl text-navy mb-4">Contact Us</h2>
                <p className="text-text-mid leading-relaxed">
                  If you have any questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <div className="bg-cream rounded-xl p-6 mt-4">
                  <p className="text-navy font-semibold">Cali Moms Connect</p>
                  <p className="text-text-mid mt-2">Email: <a href="mailto:hello@calimomsconnect.com" className="text-teal hover:text-teal-deep underline">hello@calimomsconnect.com</a></p>
                  <p className="text-text-mid mt-1">Location: California, United States</p>
                </div>
              </section>

            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
